<template>
  <v-dialog
      :value="value"
      :eager="eager"
      width="80%"
      :content-class="`simple-dialog ${fullHeight ? 'full-height' : ''}`"
      @input="handleDialogInput">
    <v-app-bar dark :color="titleColor">
      <v-btn icon dark tabindex="1" @click="handleClose">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ titleText }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <slot name="prepend-apply-button"></slot>
        <v-btn v-if="!!applyText"
               ref="applyButton"
               @click="handleApply"
               :disabled="applyDisabled || (valid === null ? !validForm : valid === false)"
               dark text>
          <slot name="apply-button" :applyIcon="applyIcon" :applyText="applyText" :modifierKeys="modifierKeys">
            <v-icon v-if="!!applyIcon">{{ applyIcon }}</v-icon>
            {{ applyText }}
          </slot>
        </v-btn>
        <slot name="append-apply-button"></slot>
      </v-toolbar-items>
    </v-app-bar>
    <slot name="prepend" :value="value" :modifierKeys="modifierKeys"></slot>
    <v-card tile style="flex-grow: 1; overflow-y: scroll;">
      <div v-if="hasHeader" class="py-5" :style="{marginBottom: `-${containerStyle.paddingTop}`}">
        <slot name="header" :headerText="headerText" :modifierKeys="modifierKeys">
          <h3>{{ headerText }}</h3>
        </slot>
      </div>
      <v-container ref="container" :style="containerStyle">
        <v-form v-model="validForm">
          <slot :value="value"></slot>
        </v-form>
      </v-container>
      <v-card-actions v-if="hasActions">
        <v-container :style="{
          paddingTop: containerStyle.paddingTop,
          paddingRight: containerStyle.paddingRight,
          paddingBottom: containerStyle.paddingBottom,
          paddingLeft: containerStyle.paddingLeft
        }">
          <slot name="actions" :value="value"></slot>
        </v-container>
      </v-card-actions>
      <slot name="footer"></slot>
    </v-card>
    <slot name="append" :value="value" :modifierKeys="modifierKeys"></slot>
  </v-dialog>
</template>

<script>
import deepEquals from "fast-deep-equal";

export default {
  name: 'SimpleDialog',
  props: {
    /**
     * ダイアログの可視状態
     * truthy の場合にダイアログが表示状態になります
     */
    value: {
      required: true
    },
    /**
     * フォームの有効性を注入します
     */
    valid: {
      type: Boolean,
      default: null
    },
    /**
     * ダイアログのタイトルバーに表示する文言
     */
    titleText: {
      type: String,
      required: true
    },
    /**
     * ダイアログのタイトルバーの色
     */
    titleColor: {
      type: String,
      default: 'primary'
    },
    /**
     * 完了ボタンの文言
     * 空文字を指定するとボタンを除去できます
     */
    applyText: {
      type: String,
      default: '完了'
    },
    /**
     * 完了ボタンのアイコン
     */
    applyIcon: String,
    /**
     * ヘッダー部に適度な大きさで指定文言を表示します
     */
    headerText: String,
    /**
     * ダイアログ内部に余計なパディングが不必要な場合に true としてください
     */
    noPadding: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * 常に最大の高さでレイアウトされます
     */
    fullHeight: {
      type: Boolean,
      default: false
    },
    /**
     * コンポーネントのコンテンツをマウント時に強制的にレンダリングします
     */
    eager: {
      type: Boolean,
    }
  },
  data() {
    const handleClose = this.handleClose.bind(this);
    const handleApply = this.handleApply.bind(this);
    return {
      validForm: false,
      applyDisabled: false,
      willUpdateTabIndexes: false,
      dialogInfo: {
        vm: this,
        visible: false,
        close() {
          handleClose();
        },
        apply() {
          handleApply();
        }
      },
      modifierKeys: {
        alt: false,
        shift: false,
        ctrl: false,
        meta: false,
      },
    }
  },
  emits: [
    'changeModifierKeys', 'close', 'apply', 'cancel',
  ],
  provide() {
    return {
      dialog: this.dialogInfo
    };
  },
  computed: {
    containerStyle() {
      const css = {};
      if(this.noPadding) {
        Object.assign(css, {
          paddingTop: '0px',
          paddingRight: '0px',
          paddingBottom: '0px',
          paddingLeft: '0px',
          maxWidth: '100%'
        });
      } else {
        Object.assign(css, {
          paddingTop: '20px',
          paddingRight: '40px',
          paddingBottom: '25px',
          paddingLeft: '40px'
        });
      }
      return css;
    },
    hasHeader() {
      return !!this.$slots['header']?.length || !!this.headerText;
    },
    hasActions() {
      return !!this.$slots['actions']?.length;
    },
  },
  updated() {
    if(this.willUpdateTabIndexes) {
      return;
    }
    this.willUpdateTabIndexes = true;
    this.$nextTick(() => {
      this.updateTabIndexes();
      this.willUpdateTabIndexes = false;
    });
  },
  methods: {
    handleClose() {
      const modifierKeys = {...this.modifierKeys};
      const enableApply = (() => {
        this.applyDisabled = false;
      }).bind(this);
      this.applyDisabled = true;
      this.$emit('cancel', {modifierKeys, enableApply});
      this.$emit('close', false, {modifierKeys, enableApply});
    },
    handleApply() {
      const modifierKeys = {...this.modifierKeys};
      const enableApply = (() => {
        this.applyDisabled = false;
      }).bind(this);
      this.applyDisabled = true;
      this.$emit('apply', {modifierKeys, enableApply});
      this.$emit('close', true, {modifierKeys, enableApply});
    },
    handleKeyDown(e) {
      if (e.shiftKey && e.keyCode === 13) {
        e.preventDefault();
        if(this.validForm && !this.applyDisabled) {
          this.handleApply();
        }
      }
      this.modifierKeys = {
        alt: e.altKey,
        shift: e.shiftKey,
        ctrl: e.ctrlKey,
        meta: e.metaKey,
      };
    },
    handleKeyUp(e) {
      this.modifierKeys = {
        alt: e.altKey,
        shift: e.shiftKey,
        ctrl: e.ctrlKey,
        meta: e.metaKey,
      };
    },
    handleTextareaKeyDown(e) {
      this.handleKeyDown(e);
      if (e.shiftKey && e.keyCode === 13) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    handleDialogInput(value) {
      if(!value) {
        this.handleClose();
      }
    },
    updateTabIndexes() {
      let tabIndex = 2;
      const focusable = this.findKeyboardFocusableElements();
      focusable.forEach(el => {
        el.setAttribute('tabindex', `${tabIndex++}`);
      });
      focusable
        .filter(el => el.nodeName === 'TEXTAREA')
        .forEach(el => {
          el.addEventListener('keydown', this.handleTextareaKeyDown);
        });
      if(this.$refs.applyButton) {
        this.$refs.applyButton.$el.setAttribute('tabindex', `${tabIndex}`);
      }
    },
    findKeyboardFocusableElements() {
      const containerEl = this.$refs.container;
      if(!containerEl) {
        return []
      }
      return [...containerEl.querySelectorAll('a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])')]
          .filter(el => !el.hasAttribute('disabled'))
    }
  },
  watch: {
    value(value) {
      if (value) {
        this.$emit('open');
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
      } else {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
      }
      this.dialogInfo.visible = !!value;
      this.applyDisabled = !value;
    },
    modifierKeys(newModifierKeys, oldModifierKeys) {
      if(!deepEquals(newModifierKeys, oldModifierKeys)) {
        this.$emit('changeModifierKeys', newModifierKeys);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .simple-dialog {
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  &.full-height {
    height: 90%;
  }

  > .v-app-bar {
    flex-grow: initial;
  }
}
</style>
